import { useDeferredValue, useState } from "react";
import { ChevronRight, Loader2, Plus, Search } from "lucide-react";
import { keepPreviousData } from "@tanstack/react-query";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/lib/auth";
import { usePermissao } from "@/lib/use-permissao";
import { Avatar } from "@/components/Avatar";
import { PatientFormDialog } from "@/components/patients/PatientFormDialog";
import { PatientSummaryDialog, type PatientSummaryTarget } from "@/components/shared/PatientSummaryDialog";
import { SemPermissao } from "./SemPermissao";

/** Pacientes da organização em foco: busca, lista, cadastro e resumo de cada um. */
export function PainelPacientes() {
  const { user } = useAuth();
  const podeVer = usePermissao("patients.view");
  const podeCadastrar = usePermissao("patients.manage");
  const [busca, setBusca] = useState("");
  const termo = useDeferredValue(busca.trim());
  const [cadastrando, setCadastrando] = useState(false);
  const [resumo, setResumo] = useState<PatientSummaryTarget | null>(null);

  const lista = trpc.patient.list.useQuery(
    { search: termo || undefined, active: true, take: 30 },
    { enabled: podeVer, placeholderData: keepPreviousData },
  );

  if (!podeVer) return <SemPermissao area="os pacientes" />;
  const pacientes = (lista.data?.patients ?? []) as { id: string; name: string; phone: string | null; avatarUrl: string | null }[];
  const total = lista.data?.total ?? 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <input
            type="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar paciente..."
            className="h-10 w-full rounded-md border border-zinc-200 bg-white pl-9 pr-3 text-sm dark:border-border dark:bg-card"
          />
        </div>
        {podeCadastrar && (
          <button
            type="button"
            onClick={() => setCadastrando(true)}
            className="inline-flex h-10 items-center gap-1.5 rounded-md bg-brand-600 px-3 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700"
          >
            <Plus className="h-4 w-4" />
            Novo paciente
          </button>
        )}
      </div>

      {lista.isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-brand-600" />
        </div>
      ) : pacientes.length === 0 ? (
        <p className="py-8 text-center text-sm text-zinc-500 dark:text-muted-foreground">
          {termo ? "Nenhum paciente encontrado." : "Nenhum paciente cadastrado."}
        </p>
      ) : (
        <ul className="divide-y divide-zinc-100 rounded-lg border border-zinc-200 dark:divide-border dark:border-border">
          {pacientes.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() =>
                  setResumo({
                    patientId: p.id,
                    patientName: p.name,
                    organizationId: user?.activeOrganization?.id ?? "",
                    organizationName: user?.activeOrganization?.name ?? null,
                  })
                }
                className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-zinc-50 dark:hover:bg-muted"
              >
                <Avatar src={p.avatarUrl} name={p.name} size="sm" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-zinc-900 dark:text-foreground">{p.name}</span>
                  {p.phone && <span className="block text-xs text-zinc-500 dark:text-muted-foreground">{p.phone}</span>}
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-zinc-400" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {total > pacientes.length && (
        <p className="text-center text-xs text-zinc-500 dark:text-muted-foreground">
          Mostrando {pacientes.length} de {total}. Refine a busca ou abra a página completa.
        </p>
      )}

      <PatientFormDialog open={cadastrando} onClose={() => setCadastrando(false)} />
      <PatientSummaryDialog target={resumo} onClose={() => setResumo(null)} />
    </div>
  );
}
